/**
 * LEVANTAMIENTO DE RIEGO — la guía y el plano, en la misma pantalla.
 * =============================================================================
 *
 * Es la primera rebanada vertical de la tesis del 2026-08-13: la aplicación no calcula
 * para un experto, **guía a alguien que no lo es**. Por eso la guía no está en un
 * manual ni en una pantalla previa: vive al lado del dibujo, paso por paso, y la teoría
 * aparece detrás de un toque en el momento en que hace falta (Principio 8).
 *
 * Las tres cosas que este archivo tiene que sostener:
 *
 * 1. **Que nadie tenga que saber hidráulica.** Se pregunta lo observable —litros de la
 *    cubeta y segundos que tardó— y el sistema traduce a caudal (Principio 7).
 * 2. **Que el cliente vea su casa dibujada.** El plano es argumento de venta, no solo
 *    insumo de cálculo.
 * 3. **Que todo número se pueda auditar.** Cada renglón del resultado dice de qué
 *    medición y de qué regla salió (Principio 9).
 */

import { useState, useMemo, useEffect, useRef } from 'react'
import Jardin, { lados } from './Jardin.jsx'
import Sello from './Sello.jsx'
import Materiales from './Materiales.jsx'
import { GUIA_RIEGO, conversiones } from '../motor/guia.js'
import { ASPERSORES, calcular } from '../motor/riego.js'

const mx = n => '$' + Math.round(n || 0).toLocaleString('es-MX')

/** Qué modo del lienzo corresponde a cada paso de la guía. */
const MODO_POR_PASO = {
  toma: 'toma', caudal: 'ver', presion: 'ver', trazo: 'trazo',
  obstaculos: 'ver', colocar: 'colocar', calcular: 'ver'
}

export default function Riego ({ atras, onGuardar, insumos = [], preferencias = {}, plano = null, onPlano, semilla = [] }) {
  const [i, setI] = useState(0)
  const [porQueAbierto, setPorQueAbierto] = useState(false)

  /* EL PLANO VIVE EN LA CASA (escalón 9a, 2026-08-20). Si la casa ya tiene el
     jardín trazado —de esta visita o de una anterior—, el levantamiento arranca
     con él en vez de en blanco; y cada vez que el trazo queda cerrado, se le
     devuelve a la casa. El segundo rubro encuentra lo que el primero dibujó. */
  const [toma, setToma] = useState(plano?.toma ?? null)
  const [poligono, setPoligono] = useState(plano?.poligono ?? [])
  /* El cierre del trazo es una decisión del operador, no una consecuencia de haber
     puesto tres esquinas. Ver la nota en `Jardin.jsx`. */
  const [cerrado, setCerrado] = useState(!!plano?.cerrado)
  const heredado = !!(plano?.poligono?.length >= 3)
  const ultimoPlano = useRef(JSON.stringify(plano || null))
  useEffect(() => {
    if (!onPlano || !cerrado || poligono.length < 3) return
    const sig = { poligono, cerrado, toma }
    const clave = JSON.stringify(sig)
    if (clave === ultimoPlano.current) return
    ultimoPlano.current = clave
    onPlano(sig)
  }, [poligono, cerrado, toma, onPlano])
  /* Los aspersores colocados en el plano de la casa (E7-lite) llegan como semilla. */
  const [aspersores, setAspersores] = useState(semilla)
  const [modelo, setModelo] = useState('van-15')

  // Lo observable, tal como se captura en campo.
  const [cubeta, setCubeta] = useState('19')
  const [segundos, setSegundos] = useState('')
  const [presion, setPresion] = useState('')
  const [desnivel, setDesnivel] = useState('')
  const [notas, setNotas] = useState('')

  const paso = GUIA_RIEGO.pasos[i]
  const modo = MODO_POR_PASO[paso.id] || 'ver'

  const caudal = conversiones.caudal(parseFloat(cubeta) || 0, parseFloat(segundos) || 0)

  const resultado = useMemo(() => {
    if (!cerrado || poligono.length < 3 || !aspersores.length || !toma || !caudal) return null
    return calcular({ poligono, aspersores, toma, caudalDisponible: caudal,
                      alturaEstatica: parseFloat(desnivel) || 0,
                      insumos, holguraObra: preferencias.holguraObra ?? 1.10 })
  }, [poligono, aspersores, toma, caudal, cerrado, desnivel, insumos, preferencias.holguraObra])

  /** Qué le falta a este paso para poder avanzar. Se dice, no se bloquea en silencio. */
  const falta = (() => {
    if (paso.id === 'toma' && !toma) return 'Toca el plano donde está la llave.'
    if (paso.id === 'caudal' && !caudal) return 'Escribe cuántos segundos tardó en llenarse.'
    if (paso.id === 'trazo' && poligono.length < 3) return 'Marca al menos tres esquinas.'
    if (paso.id === 'trazo' && !cerrado) return 'Cierra el trazo para continuar.'
    if (paso.id === 'colocar' && !aspersores.length) return 'Coloca al menos un aspersor.'
    return null
  })()

  const avanzar = () => { setPorQueAbierto(false); setI(Math.min(i + 1, GUIA_RIEGO.pasos.length - 1)) }
  const volver = () => { setPorQueAbierto(false); setI(Math.max(i - 1, 0)) }

  return (
    <div className="app">
      <div className="bar">
        <button className="ib ico" onClick={atras} aria-label="Regresar">
          <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor"
            strokeWidth="1.5" strokeLinecap="round" aria-hidden="true"><path d="M15 5l-7 7 7 7" /></svg>
        </button>
        <h1>Riego<span className="crumb">{GUIA_RIEGO.duracion} · paso {i + 1} de {GUIA_RIEGO.pasos.length}</span></h1>
      </div>

      <main>
        {/* Avance. Los pasos ya hechos se pueden revisitar; los siguientes no se adelantan:
            el orden importa —no se puede colocar riego antes de saber cuánta agua hay—. */}
        <ol className="avance" aria-label="Pasos del levantamiento">
          {GUIA_RIEGO.pasos.map((p, n) => (
            <li key={p.id}>
              <button type="button"
                className={`hito ${n === i ? 'ahora' : ''} ${n < i ? 'hecho' : ''}`}
                onClick={() => n <= i && setI(n)} disabled={n > i}
                aria-current={n === i ? 'step' : undefined}
                aria-label={`Paso ${n + 1}: ${p.titulo}`} />
            </li>
          ))}
        </ol>

        <div className="paso-guia">
          <h2 className="sec" style={{ marginTop: 0 }}>{paso.titulo}</h2>
          {paso.instrumento && (
            <p className="pastilla" style={{ marginBottom: 'var(--e4)' }}>{paso.instrumento}</p>
          )}

          <ol className="comos">
            {paso.como.map((c, n) => <li key={n}>{c}</li>)}
          </ol>

          {paso.ojo && (
            <div className="warn leible" style={{ marginTop: 'var(--e5)' }}>
              <b>Ojo:</b> {paso.ojo}
            </div>
          )}

          {/* La teoría, detrás de un toque. Principio 8: donde se usa, no en un manual. */}
          <button type="button" className="discreto" style={{ marginTop: 'var(--e3)' }}
            onClick={() => setPorQueAbierto(!porQueAbierto)} aria-expanded={porQueAbierto}>
            {porQueAbierto ? 'Ocultar el porqué' : '¿Por qué se hace así?'}
          </button>
          {porQueAbierto && (
            <div className="nota leible" style={{ marginTop: 'var(--e4)' }}>
              <p style={{ margin: 0 }}>{paso.porQue}</p>
            </div>
          )}

          {/* El guion de venta lleva forma propia y NO el estilo de acento: con él se
              veía igual que la traducción de una medición, y son cosas distintas —una
              es un resultado del sistema, la otra es lo que sale de tu boca—. */}
          {paso.decir && (
            <blockquote className="guion leible">
              <span>Dile al cliente</span>
              <p>{paso.decir}</p>
            </blockquote>
          )}
        </div>

        {/* ---- Captura propia de cada paso ---- */}

        {paso.id === 'caudal' && (
          <div className="captura">
            <div className="two">
              <label className="f">
                <span>Litros de la cubeta</span>
                <input type="number" inputMode="decimal" value={cubeta}
                  onChange={e => setCubeta(e.target.value)} />
              </label>
              <label className="f">
                <span>Segundos que tardó</span>
                <input type="number" inputMode="decimal" value={segundos} placeholder="Ej. 24"
                  onChange={e => setSegundos(e.target.value)} />
              </label>
            </div>
            {/* La traducción de lo observable a lo técnico, a la vista. */}
            {caudal > 0 && (
              <div className="nota acento" style={{ margin: 0 }}>
                Eso son <b>{caudal.toFixed(0)} litros por minuto</b> disponibles.
                {caudal < 20 && ' Es poco: el jardín va a regar por turnos, en más zonas.'}
                {caudal >= 45 && ' Es holgado: probablemente quepan pocas zonas grandes.'}
              </div>
            )}
          </div>
        )}

        {paso.id === 'presion' && (
          <div className="captura">
            <label className="f" style={{ marginBottom: 0 }}>
              <span>Presión con el agua corriendo, en bar</span>
              <input type="number" inputMode="decimal" value={presion} placeholder="Ej. 2.5"
                onChange={e => setPresion(e.target.value)} />
              <span>Si no traes manómetro, déjalo vacío: queda anotado como pendiente y el
                    cálculo lo declara.</span>
            </label>
            {/* El desnivel es la única entrada del módulo que ninguna tabla puede dar: se
                ve, se estima a ojo y cambia la bomba. Por eso se pregunta aquí y no se
                supone. */}
            <label className="f" style={{ marginBottom: 0, marginTop: 'var(--e4)' }}>
              <span>Desnivel entre la llave y la parte más alta del jardín, en metros</span>
              <input type="number" inputMode="decimal" value={desnivel} placeholder="Ej. 0"
                onChange={e => setDesnivel(e.target.value)} />
              <span>Si el jardín está al mismo nivel que la llave, es cero. Si el aspersor
                    más lejano queda en una terraza o en un talud, mide cuánto sube: cada
                    metro de subida se lo cobra a la bomba igualito que un metro de presión.</span>
            </label>
          </div>
        )}

        {paso.id === 'obstaculos' && (
          <div className="captura">
            <label className="f" style={{ marginBottom: 0 }}>
              <span>Obstáculos, pendiente y tipo de planta por zona</span>
              <textarea rows="3" value={notas} onChange={e => setNotas(e.target.value)}
                placeholder="Ej. Árbol grande al centro. La franja del muro norte es de suculentas." />
            </label>
          </div>
        )}

        {paso.id === 'colocar' && (
          <>
            <div className="sec">Qué vas a colocar</div>
            <div className="grid productos sangre">
              {ASPERSORES.map(a => (
                <button type="button" key={a.id}
                  className={`gc gp modelo ${modelo === a.id ? 'sel' : ''}`}
                  onClick={() => setModelo(a.id)} aria-pressed={modelo === a.id}>
                  <span className="disco" style={{ '--r': a.radio }} aria-hidden="true" />
                  <div className="b">
                    <div className="n">{a.nombre}</div>
                    <div className="s">{a.gasto} L/min · alcance {a.radio} m {a.sinVerificar && <Sello />}</div>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}

        {/* ---- El plano, siempre visible salvo en los pasos de captura pura ---- */}
        <div className="sec">El plano</div>
        {heredado && modo === 'trazo' && (
          <div className="nota acento">
            <b>El jardín ya estaba trazado en esta casa.</b> Viene de un levantamiento
            anterior: revísalo, corrige una esquina si cambió algo, o empieza de nuevo.
          </div>
        )}
        <Jardin
          poligono={poligono} setPoligono={setPoligono}
          aspersores={aspersores} setAspersores={setAspersores}
          toma={toma} setToma={setToma}
          cerrado={cerrado} setCerrado={setCerrado}
          modo={modo} modeloActivo={modelo} caudalDisponible={caudal} />

        {modo === 'trazo' && poligono.length > 0 && (
          <>
            <div className="captura-acc" style={{ marginTop: 'var(--e4)' }}>
              {!cerrado && <button className="ib sm" onClick={() => setPoligono(poligono.slice(0, -1))}>
                Deshacer esquina
              </button>}
              {!cerrado && poligono.length >= 3 && (
                <button className="ib sm pri" onClick={() => setCerrado(true)}>Cerrar el trazo</button>
              )}
              {cerrado && (
                <button className="ib sm" onClick={() => setCerrado(false)}>Seguir agregando esquinas</button>
              )}
              <button className="ib sm" onClick={() => { setPoligono([]); setCerrado(false) }}>Empezar de nuevo</button>
            </div>
            <Medidas poligono={poligono} setPoligono={setPoligono} cerrado={cerrado} />
          </>
        )}
        {modo === 'colocar' && aspersores.length > 0 && (
          <div className="captura-acc" style={{ marginTop: 'var(--e4)' }}>
            <button className="ib sm" onClick={() => setAspersores(aspersores.slice(0, -1))}>
              Quitar el último
            </button>
          </div>
        )}

        {/* ---- El resultado ---- */}
        {paso.id === 'calcular' && (
          resultado ? <Resultado r={resultado} caudal={caudal} presion={presion} onGuardar={onGuardar} />
                    : <div className="empty">
                        <b>Todavía no se puede calcular.</b>
                        <p>Faltan la toma, el trazo del jardín, al menos un aspersor y la
                           medición del caudal.</p>
                      </div>
        )}
      </main>

      <div className="fab"><div className="in">
        <div className="cuadro">
          <div className="celda">
            <span className="k">Paso</span>
            <span className="v">{i + 1} de {GUIA_RIEGO.pasos.length}</span>
          </div>
          {caudal > 0 && (
            <div className="celda oculta-chica">
              <span className="k">Caudal</span>
              <span className="v">{caudal.toFixed(0)} L/min</span>
            </div>
          )}
          {resultado && (
            <div className="celda importe">
              <span className="k">Zonas</span>
              <span className="v">{resultado.zonas.length}</span>
            </div>
          )}
        </div>
        {i > 0 && <button className="gh" onClick={volver} aria-label="Paso anterior">←</button>}
        {i < GUIA_RIEGO.pasos.length - 1 && (
          <button onClick={avanzar} disabled={!!falta} title={falta || ''}>
            {falta || 'Siguiente'}
          </button>
        )}
      </div></div>
    </div>
  )
}

/* ============================ Las medidas de cada lado ============================ */

/**
 * Escribir la medida real de un lado.
 *
 * Trazar con el dedo sobre retícula da media unidad de precisión, que sirve para la
 * forma pero no para un presupuesto: de la geometría salen los metros de tubo. Aquí se
 * corrige con el flexómetro sin volver a dibujar.
 *
 * **Cómo se comporta, dicho de frente:** escribir la medida de un lado mueve la esquina
 * del FINAL de ese lado, conservando su dirección. Las demás esquinas no se tocan, así
 * que el lado siguiente cambia de largo — es la consecuencia inevitable de fijar uno
 * solo, y es preferible a que el sistema decida por su cuenta qué otra cosa mover.
 * Se corrigen en orden, del primer lado al último, y al final cuadra.
 */
function Medidas ({ poligono, setPoligono, cerrado }) {
  /* Todos los campos son CONTROLADOS, y el texto en edición vive aparte en `borrador`.
     La primera versión mezclaba `defaultValue` en unos campos y `value` en el de cierre,
     y eso trajo dos fallos a la vez: React advertía por pasar de controlado a no
     controlado, y —peor— un campo con `defaultValue` NO se actualiza cuando cambia el
     polígono, así que la lista mostraba 14.42 m mientras la cota del plano ya decía
     12.0 m. Un número viejo en un formulario de medidas es exactamente el error que
     este paso existe para evitar. */
  const [borrador, setBorrador] = useState({})
  const misLados = lados(poligono, cerrado)
  if (!misLados.length) return null

  const soltarBorrador = i => setBorrador(b => { const n = { ...b }; delete n[i]; return n })

  const fijar = (i, largo) => {
    const l = misLados[i]
    const n = Math.hypot(l.b.x - l.a.x, l.b.y - l.a.y)
    if (!n || !largo || largo <= 0) return
    const ux = (l.b.x - l.a.x) / n, uy = (l.b.y - l.a.y) / n
    const destino = (i + 1) % poligono.length
    // El último lado de un polígono cerrado vuelve al origen: fijarlo movería la primera
    // esquina y arrastraría todo el trazo. Se deja como resultado, no como dato.
    if (cerrado && destino === 0) return
    setPoligono(poligono.map((p, k) =>
      k === destino ? { x: +(l.a.x + ux * largo).toFixed(2), y: +(l.a.y + uy * largo).toFixed(2) } : p))
  }

  return (
    <>
      <div className="sec" style={{ marginBottom: 'var(--e3)' }}>Medida de cada lado</div>
      <p className="s leible" style={{ margin: '0 0 var(--e4)' }}>
        Si ya lo mediste con flexómetro, escríbelo aquí. Corrige la esquina del final de
        ese lado y deja las demás donde están, así que conviene ir en orden.
      </p>
      <div className="lados">
        {misLados.map((l, i) => {
          const esCierre = cerrado && (i + 1) % poligono.length === 0
          return (
            <div className="lado" key={l.i}>
              <span className="k">Lado {i + 1}</span>
              {esCierre
                ? <><input value={l.largo.toFixed(2)} readOnly
                      aria-label={`Lado ${i + 1}, cierre del polígono`} />
                    <span className="u">m · cierra solo</span></>
                : <><input type="number" inputMode="decimal" step="0.01"
                      value={borrador[i] ?? l.largo.toFixed(2)}
                      onChange={e => setBorrador({ ...borrador, [i]: e.target.value })}
                      onBlur={e => { fijar(i, parseFloat(e.target.value)); soltarBorrador(i) }}
                      onKeyDown={e => e.key === 'Enter' && e.target.blur()}
                      aria-label={`Medida del lado ${i + 1} en metros`} />
                    <span className="u">m</span></>}
            </div>
          )
        })}
      </div>
    </>
  )
}

/* ============================ El resultado ============================ */

function Resultado ({ r, caudal, presion, onGuardar }) {
  const [auditando, setAuditando] = useState(false)
  return (
    <>
      <div className="sec">Lo que necesita este jardín</div>

      {r.cobertura.porcentaje < 99 && (
        <div className="warn leible">
          <b>Quedan {r.cobertura.seco.toFixed(1)} m² sin regar.</b> Están en rojo en el plano.
          Vuelve al paso anterior y acomoda o agrega aspersores antes de proponer.
        </div>
      )}

      <div className="plan total">
        <div className="hd">
          <div className="k">Zonas de riego</div>
          <div className="v">{r.zonas.length}</div>
          <div className="d">
            Salen del caudal que mediste, no de una preferencia: {caudal.toFixed(0)} L/min
            disponibles y un pico de {r.gastoPico.toFixed(0)} L/min por zona.
          </div>
        </div>
      </div>

      <Materiales lista={r.materiales} auditando={auditando} />

      <div className="sec">Dimensionamiento</div>
      <div className="ln"><span>Tubería principal</span><b>{r.diametro.pulgadas}</b></div>
      <div className="ln">
        <span>Velocidad del agua</span>
        <b>{r.diametro.velocidad.toFixed(2)} m/s</b>
      </div>
      <div className="ln"><span>Recorrido de tubería</span><b>{r.red.metros.toFixed(1)} m</b></div>
      {r.bomba && (
        <>
          <div className="ln">
            <span>Altura manométrica total</span>
            <b>{r.bomba.alturaTotal.toFixed(1)} mca</b>
          </div>
          {/* Se nombra el modelo, no el caballaje: dos bombas del mismo HP tienen curvas
              distintas, y con el HP solo no se puede ir a comprar. */}
          <div className="ln big">
            <span>Bomba</span>
            <b>{r.bomba.bomba
              ? `${r.bomba.bomba.modelo} · ${r.bomba.bomba.hp} HP`
              : 'Ninguna de la serie alcanza'}</b>
          </div>
          {r.bomba.bomba && (
            <div className="ln">
              <span>Holgura sobre el punto de trabajo</span>
              <b>{r.bomba.bomba.holgura.toFixed(1)} m</b>
            </div>
          )}
          {r.bomba.fueraDeSerie && (
            <div className="warn leible" style={{ marginTop: 'var(--e4)' }}>
              El sistema pide <b>{r.bomba.alturaTotal.toFixed(1)} mca</b> a{' '}
              <b>{r.gastoPico} L/min</b>, y ninguna bomba doméstica de la serie llega hasta
              ahí. Lo que corresponde no es una bomba más grande: es <b>partir el riego en
              más zonas</b>, para que cada turno pida menos caudal.
            </div>
          )}
        </>
      )}

      {/* Principio 9: toda decisión automática es auditable. */}
      <button type="button" className="discreto" style={{ marginTop: 'var(--e5)' }}
        onClick={() => setAuditando(!auditando)} aria-expanded={auditando}>
        {auditando ? 'Ocultar de dónde sale cada número' : 'Ver de dónde sale cada número'}
      </button>

      {auditando && (
        <div className="nota leible" style={{ marginTop: 'var(--e4)' }}>
          <p style={{ marginTop: 0 }}>
            <b>Zonas:</b> se reparten los aspersores por cercanía mientras la suma de su gasto
            quepa en el 90% del caudal medido. La reserva del 10% existe para no trabajar al
            límite de la toma.
          </p>
          <p>
            <b>Diámetro:</b> el más chico cuya velocidad se mantenga bajo 1.5 m/s. Por encima
            aparecen ruido, erosión y golpe de ariete al cerrar una válvula.
          </p>
          <p>
            <b>Tubería:</b> árbol de recorrido mínimo desde la toma, medido en ángulo recto
            porque la zanja se excava así. Un codo por cada quiebre, una te por cada nodo del
            que sale más de una rama.
          </p>
          <p style={{ marginBottom: 0 }}>
            <b>Bomba:</b> se suma altura estática + fricción por Hazen-Williams (C=150 para
            PVC) + accesorios + la presión que exige el aspersor, y con ese punto —caudal y
            altura— se busca en la tabla de prestaciones del fabricante la bomba más chica
            cuya curva pase por encima. No se calcula un caballaje y luego se redondea: dos
            bombas del mismo HP tienen curvas distintas.
          </p>
        </div>
      )}

      <div className="prov leible" style={{ marginTop: 'var(--e5)' }}>
        <b>De dónde salen los datos.</b> Las boquillas, de la carta de rendimiento de
        Rain Bird. El espaciamiento, del criterio de cabeza con cabeza. La bomba, de la tabla
        de prestaciones de Pedrollo. La geometría, la hidráulica y el recorrido son exactos.
        Lo único que el sistema no puede saber solo es el <b>desnivel entre la llave y la
        parte más alta del jardín</b>: si no se mide, se toma como cero.
        {!presion && ' Además, la presión de la toma quedó sin medir en esta visita.'}
      </div>

      {onGuardar && (
        <button className="ib pri w" style={{ marginTop: 'var(--e6)' }}
          onClick={() => onGuardar(r)}>Agregar a la cotización</button>
      )}
    </>
  )
}
